import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  Timestamp,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { API_BASE } from '../utils/apiBase';
//  SECURITY: Import device fingerprint service for signup tracking

import { deviceFingerprintService } from './security';

export interface UserProfile {
  uid: string;
  email: string;
  username: string;
  fullName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  bio?: string;
  photoURL?: string;
  avatarUrl?: string;           //  NEW: Avatar URL (DiceBear or custom)

  avatarStyle?: string;         //  NEW: 'dicebear-adventurer' | 'dicebear-avataaars' | 'dicebear-bottts' | 'custom'

  showFullName?: boolean;
  referralCode: string;
  referrerId?: string;
  rootAgentId?: string;         // The root agent who gets rake credit
  isAgent?: boolean;            // Quick flag for agent status
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: Date;
  tutorialCompleted?: boolean;  //  NEW: Tutorial completion flag

  tutorialSkipped?: boolean;    //  NEW: Tutorial skipped flag

  tutorialCompletedAt?: Timestamp; //  NEW: When tutorial was completed

  kycStatus: 'pending' | 'verified' | 'rejected';
  createdAt: Timestamp;
  updatedAt: Timestamp;
  followersCount?: number;
  followingCount?: number;
  stats?: {
    gamesPlayed: number;
    totalWinnings: number;
    handsWon: number;
    level: number;
  };
  personalLimits?: {
    dailyDepositLimit?: number;
    weeklyDepositLimit?: number;
    monthlyDepositLimit?: number;
    sessionTimeLimit?: number;
    lossLimit?: number;
    enabled: boolean;
  };
}

export interface PersonalInfoUpdate {
  fullName?: string;
  username?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  bio?: string;
  photoURL?: string;
  showFullName?: boolean;
}

export interface PersonalLimitsUpdate {
  dailyDepositLimit?: number;
  weeklyDepositLimit?: number;
  monthlyDepositLimit?: number;
  sessionTimeLimit?: number;
  lossLimit?: number;
  enabled: boolean;
}

class UserService {
  private userCache = new Map<string, UserProfile>();

  /**
   * Generate unique referral code
   */
  private generateReferralCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'PS';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  // ============================================================================
  // ROOT AGENT RESOLUTION
  // ============================================================================

  /**
   * Check if a user is a designated agent
   * @param userId - User ID to check
   * @returns true if user is an active agent
   */
  async isUserAgent(userId: string): Promise<boolean> {
    try {
      const agentDoc = await getDoc(doc(db, 'agents', userId));
      return agentDoc.exists() && agentDoc.data().active === true;
    } catch (error) {
      console.error('Error checking agent status:', error);
      return false;
    }
  }

  /**
   * Resolve the root agent for a referral chain
   * 
   * Logic:
   * 1. If referrer is an agent → referrer is the rootAgentId
   * 2. If referrer has a rootAgentId → propagate that rootAgentId
   * 3. Otherwise → no agent in chain, return null
   * 
   * @param referrerId - The direct referrer's user ID
   * @returns The root agent's user ID, or null if no agent in chain
   */
  async resolveRootAgent(referrerId: string): Promise<string | null> {
    try {
      console.log(`🔍 [UserService] Resolving root agent for referrer: ${referrerId.substring(0, 8)}`);

      // Step 1: Check if the direct referrer is an agent
      const isReferrerAgent = await this.isUserAgent(referrerId);
      
      if (isReferrerAgent) {
        console.log(`   ✅ Referrer IS an agent - they become rootAgentId`);
        return referrerId;
      }

      // Step 2: Check if the referrer has a rootAgentId (they were referred by an agent's chain)
      const referrerDoc = await getDoc(doc(db, 'users', referrerId));
      
      if (referrerDoc.exists()) {
        const referrerData = referrerDoc.data();
        
        if (referrerData.rootAgentId) {
          console.log(`   ✅ Referrer has rootAgentId: ${referrerData.rootAgentId.substring(0, 8)}`);
          return referrerData.rootAgentId;
        }
      }

      // Step 3: No agent in the chain
      console.log(`   ℹ️ No agent found in referral chain`);
      return null;

    } catch (error) {
      console.error('❌ Error resolving root agent:', error);
      return null;
    }
  }

  /**
   * Find user by referral code
   *  FIXED: Uses Cloud Function to avoid Firestore permission issues

   * @param code - Referral code to search for
   * @returns User ID if found, null otherwise
   */
  async findUserByReferralCode(code: string): Promise<string | null> {
    try {
      const normalizedCode = code.toUpperCase().trim();
      console.log(`🔍 [UserService] Validating referral code via API: "${normalizedCode}"`);
      
      // Call Cloud Function endpoint (no auth required)
      const apiBase = API_BASE;
      
      const response = await fetch(
        `${apiBase}/validateReferralCode?code=${encodeURIComponent(normalizedCode)}`
      );
      
      if (!response.ok) {
        console.error(`   ❌ API error: ${response.status}`);
        return null;
      }
      
      const result = await response.json();
      
      if (result.valid && result.referrerId) {
        console.log(`   ✅ Valid code! Referrer: ${result.referrerId.substring(0, 8)}...`);
        return result.referrerId;
      }
      
      console.log(`   ❌ Invalid referral code: "${normalizedCode}"`);
      return null;

    } catch (error) {
      console.error('❌ Error validating referral code:', error);
      return null;
    }
  }

  // ============================================================================
  // CREATE USER PROFILE
  // ============================================================================

  /**
   * CREATE USER PROFILE
   *  UPDATED: Now resolves rootAgentId from referral chain

   * 
   * @param uid - Firebase user ID
   * @param email - User's email
   * @param username - Chosen username
   * @param referrerId - Direct referrer's user ID (optional)
   */
  async createUserProfile(
    uid: string,
    email: string,
    username: string,
    referrerId?: string
  ): Promise<UserProfile> {
    try {
      //  FIX: Check if profile already exists (idempotent - prevents race condition duplicates)

      const existingProfile = await this.getUserProfile(uid);
      if (existingProfile) {
        console.log(`⚠️ [UserService] Profile already exists for ${uid.substring(0, 8)}, returning existing`);
        return existingProfile;
      }

      console.log(`📝 Creating profile for ${username}`);

      //  Resolve root agent if there's a referrer

      let rootAgentId: string | null = null;
      
      if (referrerId) {
        rootAgentId = await this.resolveRootAgent(referrerId);
        
        if (rootAgentId) {
          console.log(`   🎯 Root agent resolved: ${rootAgentId.substring(0, 8)}`);
        } else {
          console.log(`   ℹ️ No agent in referral chain (friend-to-friend referral)`);
        }
      }

      const userProfile: any = {
        email,
        username: username.toLowerCase(),
        referralCode: this.generateReferralCode(),
        kycStatus: 'pending',
        showFullName: true,
        followersCount: 0,
        followingCount: 0,
        onboardingCompleted: false,  //  NEW: Triggers onboarding redirect for new users

        tutorialCompleted: false,  //  NEW: Default to false for new users

        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        stats: {
          gamesPlayed: 0,
          totalWinnings: 0,
          handsWon: 0,
          level: 1,
        },
        personalLimits: {
          enabled: false,
        },
      };

      // Only add referrerId if it exists
      if (referrerId) {
        userProfile.referrerId = referrerId;
      }

      //  Add rootAgentId if resolved

      if (rootAgentId) {
        userProfile.rootAgentId = rootAgentId;
      }

      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, userProfile);

      const profile: UserProfile = {
        uid,
        ...userProfile,
      };

      this.userCache.set(uid, profile);
      console.log(`✅ Profile created for ${username}${rootAgentId ? ` (agent: ${rootAgentId.substring(0, 8)})` : ''}`);

      //  FIX: Create referral record in 'referrals' collection for agent dashboard

      if (referrerId) {
        try {
          // Check if referral already exists
          const existingQ = query(
            collection(db, 'referrals'),
            where('referredUserId', '==', uid)
          );
          const existingSnapshot = await getDocs(existingQ);
          
          if (existingSnapshot.empty) {
            const referralData: any = {
              referrerId,
              referredUserId: uid,
              referredUserEmail: email,
              status: 'verified',
              signupBonusPaid: false,
              rakeEarned: 0,
              handsPlayed: 0,
              createdAt: Timestamp.now(),
            };

            // Add rootAgentId if exists
            if (rootAgentId) {
              referralData.rootAgentId = rootAgentId;
            }

            // One doc per player, keyed by the root agent (or the referrer when
            // no agent is in the chain). The server books rake against this id.
            await setDoc(doc(db, 'referrals', `${rootAgentId || referrerId}_${uid}`), referralData, { merge: true });
            console.log(`✅ Referral record created for agent dashboard`);
          }
        } catch (refError) {
          console.warn('⚠️ Failed to create referral record (non-blocking):', refError);
        }
      }

      //  SECURITY: Capture device fingerprint and IP on signup (non-blocking)

      deviceFingerprintService.captureSecurityContext(uid, 'signup').catch(err => {
        console.warn('⚠️ Security context capture failed (non-blocking):', err);
      });

      return profile;
    } catch (error) {
      console.error('❌ Error creating profile:', error);
      throw error;
    }
  }

  /**
   * GET USER PROFILE
   */
  async getUserProfile(uid: string, forceRefresh = false): Promise<UserProfile | null> {
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh && this.userCache.has(uid)) {
        return this.userCache.get(uid) || null;
      }

      const userRef = doc(db, 'users', uid);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        console.warn(`⚠️ User profile not found for ${uid}`);
        return null;
      }

      const profile: UserProfile = {
        uid,
        ...snapshot.data(),
      } as UserProfile;

      this.userCache.set(uid, profile);
      return profile;
    } catch (error) {
      console.error('❌ Error getting profile:', error);
      return null;
    }
  }

  /**
   * GET USERNAME ONLY (fast path)
   */
  async getUsername(uid: string): Promise<string> {
    try {
      const cached = this.userCache.get(uid);
      if (cached) {
        return cached.username;
      }

      const userRef = doc(db, 'users', uid);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        return uid.substring(0, 8);
      }

      const data = snapshot.data();
      const username = data.username || uid.substring(0, 8);

      const profile: UserProfile = {
        uid,
        username,
        email: data.email,
        referralCode: data.referralCode,
        referrerId: data.referrerId,
        rootAgentId: data.rootAgentId,
        kycStatus: data.kycStatus || 'pending',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        photoURL: data.photoURL,
        fullName: data.fullName,
        showFullName: data.showFullName,
      };
      this.userCache.set(uid, profile);

      return username;
    } catch (error) {
      console.error('❌ Error getting username:', error);
      return uid.substring(0, 8);
    }
  }

  /**
   * GET ROOT AGENT ID FOR A USER
   */
  async getRootAgentId(uid: string): Promise<string | null> {
    try {
      // Check cache first
      const cached = this.userCache.get(uid);
      if (cached) {
        return cached.rootAgentId || null;
      }

      const userRef = doc(db, 'users', uid);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        return null;
      }

      return snapshot.data().rootAgentId || null;
    } catch (error) {
      console.error('Error getting root agent ID:', error);
      return null;
    }
  }

  /**
   * UPDATE PROFILE (general)
   */
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      // setDoc+merge instead of updateDoc: identical for existing docs, but
      // when the signup-time profile write is still in flight (or failed on
      // a congested backend), updateDoc threw 'no document to update' and
      // onboarding completion died with it. Merge self-heals that case.
      await setDoc(userRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Update cache
      const existing = this.userCache.get(uid);
      if (existing) {
        this.userCache.set(uid, {
          ...existing,
          ...updates,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Profile updated for ${uid}`);
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      throw error;
    }
  }

  /**
   * UPDATE PERSONAL INFO
   */
  async updatePersonalInfo(uid: string, info: PersonalInfoUpdate): Promise<void> {
    try {
      console.log(`📝 Updating personal info for ${uid}`);
      
      const updates: any = {
        updatedAt: Timestamp.now(),
      };

      // Only include defined fields
      if (info.fullName !== undefined) updates.fullName = info.fullName;
      if (info.username !== undefined) updates.username = info.username.toLowerCase();
      if (info.phoneNumber !== undefined) updates.phoneNumber = info.phoneNumber;
      if (info.dateOfBirth !== undefined) updates.dateOfBirth = info.dateOfBirth;
      if (info.bio !== undefined) updates.bio = info.bio;
      if (info.photoURL !== undefined) updates.photoURL = info.photoURL;
      if (info.showFullName !== undefined) updates.showFullName = info.showFullName;

      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, updates);

      // Update cache
      const existing = this.userCache.get(uid);
      if (existing) {
        this.userCache.set(uid, {
          ...existing,
          ...updates,
        });
      }

      console.log(`✅ Personal info updated for ${uid}`);
    } catch (error) {
      console.error('❌ Error updating personal info:', error);
      throw error;
    }
  }

  /**
   * UPDATE PROFILE PHOTO
   */
  async updateProfilePhoto(uid: string, photoURL: string | null): Promise<void> {
    try {
      console.log(`📸 Updating profile photo for ${uid}`);
      
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        photoURL: photoURL || null,
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(uid);
      if (existing) {
        this.userCache.set(uid, {
          ...existing,
          photoURL: photoURL || undefined,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Profile photo updated for ${uid}`);
    } catch (error) {
      console.error('❌ Error updating profile photo:', error);
      throw error;
    }
  }

  // ============================================================================
  //  NEW: AVATAR FUNCTIONS

  // ============================================================================

  /**
   * Update user's avatar with style tracking
   */
  async updateUserAvatar(
    userId: string, 
    avatarUrl: string, 
    avatarStyle: string = 'custom'
  ): Promise<void> {
    try {
      console.log(`🎭 Updating avatar for ${userId} (style: ${avatarStyle})`);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        photoURL: avatarUrl,
        avatarUrl: avatarUrl,
        avatarStyle: avatarStyle,
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(userId);
      if (existing) {
        this.userCache.set(userId, {
          ...existing,
          photoURL: avatarUrl,
          avatarUrl: avatarUrl,
          avatarStyle: avatarStyle,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Avatar updated for ${userId}`);
    } catch (error) {
      console.error('❌ Error updating avatar:', error);
      throw error;
    }
  }

  /**
   * Get user's avatar URL
   */
  async getUserAvatar(userId: string): Promise<string | null> {
    try {
      const profile = await this.getUserProfile(userId);
      return profile?.avatarUrl || profile?.photoURL || null;
    } catch (error) {
      console.error('Error getting user avatar:', error);
      return null;
    }
  }

  // ============================================================================
  //  NEW: TUTORIAL FUNCTIONS

  // ============================================================================

  /**
   * Mark tutorial as completed
   */
  async markTutorialCompleted(userId: string): Promise<void> {
    try {
      console.log(`🎓 Marking tutorial completed for ${userId}`);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorialCompleted: true,
        tutorialCompletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(userId);
      if (existing) {
        this.userCache.set(userId, {
          ...existing,
          tutorialCompleted: true,
          tutorialCompletedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Tutorial marked complete for ${userId}`);
    } catch (error) {
      console.error('❌ Error marking tutorial complete:', error);
      throw error;
    }
  }

  /**
   * Mark tutorial as skipped
   */
  async markTutorialSkipped(userId: string): Promise<void> {
    try {
      console.log(`⏭️ Marking tutorial skipped for ${userId}`);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorialCompleted: true,
        tutorialSkipped: true,
        tutorialCompletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(userId);
      if (existing) {
        this.userCache.set(userId, {
          ...existing,
          tutorialCompleted: true,
          tutorialSkipped: true,
          tutorialCompletedAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Tutorial marked skipped for ${userId}`);
    } catch (error) {
      console.error('❌ Error marking tutorial skipped:', error);
      throw error;
    }
  }

  /**
   * Check if user has completed tutorial
   */
  async hasTutorialCompleted(userId: string): Promise<boolean> {
    try {
      const profile = await this.getUserProfile(userId);
      return profile?.tutorialCompleted === true;
    } catch (error) {
      console.error('Error checking tutorial status:', error);
      return false;
    }
  }

  /**
   * Reset tutorial for user (for testing or replay)
   */
  async resetTutorial(userId: string): Promise<void> {
    try {
      console.log(`🔄 Resetting tutorial for ${userId}`);
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        tutorialCompleted: false,
        tutorialSkipped: false,
        tutorialCompletedAt: null,
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(userId);
      if (existing) {
        this.userCache.set(userId, {
          ...existing,
          tutorialCompleted: false,
          tutorialSkipped: false,
          tutorialCompletedAt: undefined,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Tutorial reset for ${userId}`);
    } catch (error) {
      console.error('❌ Error resetting tutorial:', error);
      throw error;
    }
  }

  // ============================================================================
  // OTHER FUNCTIONS
  // ============================================================================

  /**
   * UPDATE PERSONAL LIMITS (responsible gaming)
   */
  async updatePersonalLimits(uid: string, limits: PersonalLimitsUpdate): Promise<void> {
    try {
      console.log(`🔒 Updating personal limits for ${uid}`);
      
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        personalLimits: limits,
        updatedAt: Timestamp.now(),
      });

      // Update cache
      const existing = this.userCache.get(uid);
      if (existing) {
        this.userCache.set(uid, {
          ...existing,
          personalLimits: limits,
          updatedAt: Timestamp.now(),
        });
      }

      console.log(`✅ Personal limits updated for ${uid}`);
    } catch (error) {
      console.error('❌ Error updating personal limits:', error);
      throw error;
    }
  }

  /**
   * CHECK USERNAME AVAILABILITY
   */
  async isUsernameAvailable(username: string, currentUid?: string): Promise<boolean> {
    try {
      // This is a simplified check - in production you'd want a Cloud Function
      // that can query with proper indexing
      const normalizedUsername = username.toLowerCase().trim();
      
      // For now, we'll just check if it's different from current
      if (currentUid) {
        const currentProfile = await this.getUserProfile(currentUid);
        if (currentProfile?.username === normalizedUsername) {
          return true; // User is keeping their own username
        }
      }
      
      // Note: A proper implementation would need a Cloud Function or
      // a usernames collection to check availability efficiently
      return true;
    } catch (error) {
      console.error('❌ Error checking username:', error);
      return false;
    }
  }

  /**
   * BATCH GET USERNAMES (for player lists)
   */
  async getUsernames(uids: string[]): Promise<{ [uid: string]: string }> {
    try {
      const result: { [uid: string]: string } = {};

      for (const uid of uids) {
        result[uid] = await this.getUsername(uid);
      }

      return result;
    } catch (error) {
      console.error('❌ Error getting usernames:', error);
      return {};
    }
  }

  /**
   * GET USER DISPLAY INFO (for game tables)
   */
  async getUserDisplayInfo(uid: string): Promise<{
    username: string;
    photoURL?: string;
    displayName?: string;
  }> {
    try {
      const profile = await this.getUserProfile(uid);
      if (!profile) {
        return { username: uid.substring(0, 8) };
      }

      return {
        username: profile.username,
        photoURL: profile.photoURL || profile.avatarUrl,
        displayName: profile.showFullName ? profile.fullName : undefined,
      };
    } catch (error) {
      console.error('❌ Error getting display info:', error);
      return { username: uid.substring(0, 8) };
    }
  }

  /**
   *  Get users referred by this user (direct referrals)

   */
  async getUserReferrals(userId: string): Promise<{
    referrals: Array<{
      odid: string;
      username: string;
      photoURL?: string;
      signedUpAt: Date;
    }>;
    count: number;
  }> {
    try {
      console.log(`👥 [UserService] Getting referrals for user: ${userId.substring(0, 8)}`);

      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('referrerId', '==', userId));
      const snapshot = await getDocs(q);

      const referrals = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          odid: doc.id,
          username: data.username || 'Unknown',
          photoURL: data.photoURL,
          signedUpAt: data.createdAt?.toDate?.() || new Date(),
        };
      });

      // Sort by signup date, newest first
      referrals.sort((a, b) => b.signedUpAt.getTime() - a.signedUpAt.getTime());

      console.log(`   Found ${referrals.length} referrals`);
      return { referrals, count: referrals.length };
    } catch (error) {
      console.error('Error getting user referrals:', error);
      return { referrals: [], count: 0 };
    }
  }

  /**
   * Apply referral code to existing user (for users who signed up without a code)
   */
  async applyReferralCode(uid: string, referralCode: string): Promise<{
    success: boolean;
    message: string;
    referrerId?: string;
    rootAgentId?: string;
  }> {
    try {
      console.log(`🎟️ Applying referral code ${referralCode} to user ${uid.substring(0, 8)}`);

      // Check if user already has a referrer
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        return { success: false, message: 'User not found' };
      }

      const userData = userDoc.data();
      if (userData.referrerId) {
        return { success: false, message: 'You already have a referrer' };
      }

      // Find the referrer by code
      const referrerId = await this.findUserByReferralCode(referralCode);
      if (!referrerId) {
        return { success: false, message: 'Invalid referral code' };
      }

      // Prevent self-referral
      if (referrerId === uid) {
        return { success: false, message: 'You cannot use your own referral code' };
      }

      // Resolve root agent
      const rootAgentId = await this.resolveRootAgent(referrerId);

      // Update user profile
      const updates: any = {
        referrerId,
        updatedAt: Timestamp.now(),
      };

      if (rootAgentId) {
        updates.rootAgentId = rootAgentId;
      }

      await updateDoc(doc(db, 'users', uid), updates);

      // Invalidate cache
      this.invalidateCache(uid);

      console.log(`✅ Referral code applied: referrer=${referrerId.substring(0, 8)}, rootAgent=${rootAgentId?.substring(0, 8) || 'none'}`);

      return {
        success: true,
        message: 'Referral code applied successfully!',
        referrerId,
        rootAgentId: rootAgentId || undefined,
      };

    } catch (error) {
      console.error('❌ Error applying referral code:', error);
      return { success: false, message: 'Failed to apply referral code' };
    }
  }

  /**
   * CLEAR CACHE
   */
  clearCache(): void {
    this.userCache.clear();
    console.log(`🧹 User cache cleared`);
  }

  /**
   * INVALIDATE SINGLE USER CACHE
   */
  invalidateCache(uid: string): void {
    this.userCache.delete(uid);
  }
}

export const userService = new UserService();
export default userService;